import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { filterOptionsApi } from '../api';
import type { CascadeParams, FilterOptionsApiParams } from '../api/types';
import { useClientScope } from '../../../contexts';

type OrgCascadeOptions = Pick<CascadeParams, 'regionIds' | 'depotIds' | 'departmentIds' | 'jobRoleIds'>;

const normaliseOrgCascade = (options?: OrgCascadeOptions): OrgCascadeOptions => ({
  regionIds: options?.regionIds ?? [],
  depotIds: options?.depotIds ?? [],
  departmentIds: options?.departmentIds ?? [],
  jobRoleIds: options?.jobRoleIds ?? [],
});

export const useDepartments = (options?: { withData?: boolean; regionIds?: string[] }) => {
  const { effectiveClientId, isCentralAdmin } = useClientScope();
  const withData = options?.withData;
  const regionIds = options?.regionIds ?? [];
  return useQuery({
    queryKey: ['filter-options', 'departments', effectiveClientId, withData, regionIds],
    queryFn: () => filterOptionsApi.getDepartments(effectiveClientId, isCentralAdmin, withData, regionIds),
    staleTime: 10 * 60 * 1000,
  });
};

export const useRegions = () => {
  const { effectiveClientId } = useClientScope();
  return useQuery({
    queryKey: ['filter-options', 'regions', effectiveClientId],
    queryFn: () => filterOptionsApi.getRegions(effectiveClientId),
    staleTime: 10 * 60 * 1000,
  });
};

export const useDepots = (options?: OrgCascadeOptions) => {
  const { effectiveClientId } = useClientScope();
  const cascade = { regionIds: options?.regionIds ?? [] };
  return useQuery({
    queryKey: ['filter-options', 'depots', effectiveClientId, cascade.regionIds],
    queryFn: () => filterOptionsApi.getDepots(effectiveClientId, cascade),
    staleTime: 10 * 60 * 1000,
  });
};

export const useShiftPatterns = (options?: OrgCascadeOptions) => {
  const { effectiveClientId } = useClientScope();
  const cascade = normaliseOrgCascade(options);
  return useQuery({
    queryKey: ['filter-options', 'shift-patterns', effectiveClientId, cascade],
    queryFn: () => filterOptionsApi.getShiftPatterns(effectiveClientId, cascade),
    staleTime: 10 * 60 * 1000,
  });
};

export const useAgeRanges = (options?: OrgCascadeOptions) => {
  const { effectiveClientId } = useClientScope();
  const cascade = normaliseOrgCascade(options);
  return useQuery({
    queryKey: ['filter-options', 'age-ranges', effectiveClientId, cascade],
    queryFn: () => filterOptionsApi.getAgeRanges(effectiveClientId, cascade),
    staleTime: 10 * 60 * 1000,
  });
};

export const useSexOptions = (options?: OrgCascadeOptions) => {
  const { effectiveClientId } = useClientScope();
  const cascade = normaliseOrgCascade(options);
  return useQuery({
    queryKey: ['filter-options', 'sex', effectiveClientId, cascade],
    queryFn: () => filterOptionsApi.getSexOptions(effectiveClientId, cascade),
    staleTime: 10 * 60 * 1000,
  });
};

export const useJobRoles = (options?: { regionIds?: string[]; depotIds?: string[]; departmentIds?: string[] }) => {
  const { effectiveClientId } = useClientScope();
  const regionIds = options?.regionIds ?? [];
  const depotIds = options?.depotIds ?? [];
  const departmentIds = options?.departmentIds ?? [];
  return useQuery({
    queryKey: ['filter-options', 'job-roles', effectiveClientId, regionIds, depotIds, departmentIds],
    queryFn: () =>
      filterOptionsApi.getJobRoles(effectiveClientId, {
        regionIds,
        depotIds,
        departmentIds,
      }),
    staleTime: 10 * 60 * 1000,
  });
};

export const useUsers = (params?: FilterOptionsApiParams, options?: { enabled?: boolean }) => {
  const { effectiveClientId, isCentralAdmin } = useClientScope();
  return useQuery({
    queryKey: ['filter-options', 'users', effectiveClientId, params],
    queryFn: () =>
      filterOptionsApi.getUsers({
        ...params,
        client_id: effectiveClientId,
        bypass_anonymisation: params?.bypass_anonymisation ?? isCentralAdmin,
      }),
    enabled: (options?.enabled ?? true) && (!params?.search || params.search.length >= 2),
    staleTime: 2 * 60 * 1000,
  });
};

export const USERS_PAGE_SIZE = 50;

export const useInfiniteUsers = (params?: Omit<FilterOptionsApiParams, 'limit' | 'offset'>) => {
  const { effectiveClientId, isCentralAdmin } = useClientScope();
  return useInfiniteQuery({
    queryKey: ['filter-options', 'users-infinite', effectiveClientId, params],
    queryFn: ({ pageParam }) =>
      filterOptionsApi.getUsers({
        ...params,
        limit: USERS_PAGE_SIZE,
        offset: pageParam,
        client_id: effectiveClientId,
        bypass_anonymisation: params?.bypass_anonymisation ?? isCentralAdmin,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.length * USERS_PAGE_SIZE;
      return loaded < (lastPage.data.total ?? 0) ? loaded : undefined;
    },
    enabled: !params?.search || params.search.length >= 2,
    staleTime: 2 * 60 * 1000,
  });
};
